export function isEmailLike(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function getDisplayNameFromUser(user) {
  return (
    user?.fullName ||
    user?.name ||
    user?.displayName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    ''
  ).trim();
}

function getStaffDisplayName(member) {
  return (
    member?.fullName ||
    member?.name ||
    member?.displayName ||
    [member?.firstName, member?.lastName].filter(Boolean).join(' ') ||
    ''
  ).trim();
}

function isEmailLocalPartAlias(value, candidates = []) {
  const normalizedValue = normalize(value);
  if (!normalizedValue) return false;

  return candidates
    .map((candidate) => String(candidate || '').trim())
    .filter(isEmailLike)
    .map((email) => normalize(email.split('@')[0]))
    .some((localPart) => localPart === normalizedValue);
}

function findStaffMatch(comment = {}, staff = []) {
  const candidateIds = [
    comment.linkedWorkspaceUserId,
    comment.authorUserId,
    comment.createdBy,
    comment.createdByUserId,
    comment.userId,
  ].map(normalize).filter(Boolean);
  const candidateEmails = [
    comment.authorEmail,
    comment.email,
    isEmailLike(comment.authorName) ? comment.authorName : '',
  ].map(normalize).filter(Boolean);

  return (staff || []).find((member) => {
    const memberIds = [
      member?.linkedWorkspaceUserId,
      member?.workspaceUserId,
      member?.userId,
      member?.id,
    ].map(normalize).filter(Boolean);
    const memberEmails = [member?.email, member?.workEmail].map(normalize).filter(Boolean);

    return (
      candidateIds.some((value) => memberIds.includes(value) || memberEmails.includes(value)) ||
      candidateEmails.some((value) => memberEmails.includes(value))
    );
  });
}

function getReadableFallback(value) {
  const rawValue = String(value || '').trim();
  if (!rawValue) return '';
  if (!isEmailLike(rawValue)) return rawValue;

  return rawValue
    .split('@')[0]
    .replace(/[._-]+/g, ' ')
    .trim() || '';
}

export function resolveCommentAuthor(comment = {}, { staff = [], currentUser = null } = {}) {
  const matchedStaff = findStaffMatch(comment, staff);
  const staffName = getStaffDisplayName(matchedStaff);
  if (staffName) return staffName;

  const currentUserName = getDisplayNameFromUser(currentUser);
  const currentUserIds = [
    currentUser?.id,
    currentUser?.linkedWorkspaceUserId,
    currentUser?.workspaceUserId,
  ].map(normalize).filter(Boolean);
  const currentUserEmails = [currentUser?.email].map(normalize).filter(Boolean);
  const commentIds = [
    comment.linkedWorkspaceUserId,
    comment.authorUserId,
    comment.createdBy,
    comment.createdByUserId,
    comment.userId,
  ].map(normalize).filter(Boolean);
  const commentEmails = [comment.authorEmail, comment.email, comment.authorName]
    .map(normalize)
    .filter((value) => value && isEmailLike(value));

  if (
    currentUserName &&
    (
      commentIds.some((value) => currentUserIds.includes(value) || currentUserEmails.includes(value)) ||
      commentEmails.some((value) => currentUserEmails.includes(value)) ||
      comment.isOptimistic === true
    )
  ) {
    return currentUserName;
  }

  const authorFullName = String(comment.authorFullName || comment.authorDisplayName || '').trim();
  if (authorFullName && !isEmailLike(authorFullName)) return authorFullName;

  const authorName = String(comment.authorName || comment.author || '').trim();
  if (
    authorName &&
    !isEmailLike(authorName) &&
    !isEmailLocalPartAlias(authorName, [comment.authorUserId, comment.authorEmail, comment.email])
  ) {
    return authorName;
  }

  return getReadableFallback(comment.authorEmail || comment.email) ||
    getReadableFallback(comment.authorUserId || comment.createdBy || comment.createdByUserId) ||
    'Utente non risolto';
}

export function getAuthorInitials(displayName = '') {
  const value = String(displayName || '').trim();
  if (!value) return 'UN';

  const source = isEmailLike(value) ? value.split('@')[0].replace(/[._-]+/g, ' ') : value;
  const chunks = source.split(/\s+/).filter(Boolean);
  if (chunks.length >= 2) {
    return `${chunks[0][0]}${chunks[1][0]}`.toUpperCase();
  }

  return source.slice(0, 2).toUpperCase();
}

export function getCommentBody(comment = {}) {
  return String(comment.body ?? comment.content ?? comment.text ?? comment.testo ?? '').trim();
}

export function getCommentCreatedAt(comment = {}) {
  return comment.createdAt || comment.date || comment.data || comment.timestamp || '';
}

export function getCommentId(comment = {}) {
  return comment.id ?? comment.commentId ?? comment.noteId ?? '';
}

export function getParentCommentId(comment = {}) {
  return comment.parentId ?? comment.parentCommentId ?? comment.parentNoteId ?? null;
}

export function formatCommentDate(value) {
  if (!value) return 'Data non disponibile';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat('it-IT', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Rome',
  }).format(date);
}

export function normalizeCommentTree(comments = []) {
  const roots = [];
  const repliesByParent = new Map();

  (comments || []).forEach((comment) => {
    const parentId = getParentCommentId(comment);
    if (parentId === null || parentId === undefined || parentId === '') {
      roots.push(comment);
      return;
    }

    const key = String(parentId);
    const existing = repliesByParent.get(key) || [];
    existing.push(comment);
    repliesByParent.set(key, existing);
  });

  return roots.map((comment) => ({
    ...comment,
    replies: [
      ...(Array.isArray(comment.replies) ? comment.replies : []),
      ...(repliesByParent.get(String(getCommentId(comment))) || []),
    ],
  }));
}
